import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

// Show once per app session. sessionStorage is wiped when the user fully
// closes the browser/PWA (tab/app killed), so the popup reappears only on
// a fresh app open — not on in-session reloads or tab switches.
const SESSION_KEY = "reddy_telegram_shown_session";

function alreadyShownThisSession() {
  try {
    return sessionStorage.getItem(SESSION_KEY) === "1";
  } catch (_e) { return false; }
}

export default function TelegramJoinModal() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [telegramUrl, setTelegramUrl] = useState("https://t.me/reddyclub");
  const userId = user?.id || null;

  useEffect(() => {
    // Only show for logged-in users
    if (!userId) return;
    // Per-session gate — if already shown in this app session, skip
    if (alreadyShownThisSession()) return;
    let cancelled = false;
    api.get("/settings")
      .then(({ data }) => {
        if (!cancelled && data?.telegram_url) setTelegramUrl(data.telegram_url);
      })
      .catch(() => {})
      .finally(() => {
        if (cancelled) return;
        const t = setTimeout(() => {
          if (!cancelled) {
            try { sessionStorage.setItem(SESSION_KEY, "1"); } catch (_e) {}
            setOpen(true);
          }
        }, 800);
        return () => clearTimeout(t);
      });
    return () => { cancelled = true; };
    // Depend on user ID (stable) — not on the whole user object which can change
    // on every /auth/me refresh (visibility-change, wallet update, etc.)
  }, [userId]);

  const dismiss = () => setOpen(false);

  const join = () => {
    window.open(telegramUrl, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  if (!open || !userId) return null;

  return (
    <div
      data-testid="telegram-popup-overlay"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300"
      onClick={dismiss}
    >
      <div
        data-testid="telegram-popup"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl animate-in zoom-in-95 slide-in-from-bottom-4 duration-400"
        style={{
          background: "linear-gradient(160deg, #0f172a 0%, #1e293b 50%, #0c1424 100%)",
          border: "1px solid rgba(34, 211, 238, 0.25)",
          boxShadow: "0 25px 60px -12px rgba(34, 211, 238, 0.35), 0 0 0 1px rgba(255,255,255,0.06) inset",
        }}
      >
        <button
          onClick={dismiss}
          data-testid="telegram-popup-close"
          aria-label="Close"
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all backdrop-blur-md"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>

        <div className="absolute -top-20 -left-20 w-56 h-56 rounded-full opacity-30 blur-3xl" style={{ background: "radial-gradient(circle, #22d3ee, transparent 70%)" }} />
        <div className="absolute -bottom-24 -right-16 w-64 h-64 rounded-full opacity-25 blur-3xl" style={{ background: "radial-gradient(circle, #a855f7, transparent 70%)" }} />

        <div className="relative p-7 text-center">
          <div className="relative mx-auto mb-5 w-24 h-24">
            <div className="absolute inset-0 rounded-full animate-ping opacity-30" style={{ background: "radial-gradient(circle, #22d3ee, transparent 65%)" }} />
            <div className="absolute inset-0 rounded-full animate-pulse opacity-40" style={{ background: "radial-gradient(circle, #38bdf8, transparent 70%)", animationDelay: "0.4s" }} />
            <div
              className="relative w-24 h-24 rounded-full flex items-center justify-center shadow-2xl"
              style={{
                background: "linear-gradient(135deg, #0088cc 0%, #29b6f6 100%)",
                boxShadow: "0 12px 30px -4px rgba(41, 182, 246, 0.6)",
              }}
            >
              <svg width="48" height="48" viewBox="0 0 24 24" fill="white">
                <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"/>
              </svg>
            </div>
          </div>

          <h2 className="text-2xl font-black text-white mb-2 tracking-tight" style={{ textShadow: "0 2px 12px rgba(34, 211, 238, 0.4)" }}>
            Join Reddy Club
          </h2>
          <p className="text-cyan-300 text-sm font-semibold tracking-widest uppercase mb-4">
            Official Telegram Channel
          </p>

          <div className="mb-6 space-y-2.5 text-left">
            <Feature icon="🏆" text="Instant Result Notifications" />
            <Feature icon="💎" text="Exclusive Tips & Guessing" />
            <Feature icon="🎁" text="Special Bonuses & Offers" />
            <Feature icon="⚡" text="24×7 Live Support" />
          </div>

          <button
            onClick={join}
            data-testid="telegram-popup-join"
            className="group relative w-full py-4 rounded-2xl font-black text-white text-lg tracking-wide transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] shadow-xl overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #0088cc 0%, #29b6f6 50%, #22d3ee 100%)",
              boxShadow: "0 10px 30px -5px rgba(34, 211, 238, 0.5), 0 0 0 1px rgba(255,255,255,0.15) inset",
            }}
          >
            <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
            <span className="relative flex items-center justify-center gap-2.5">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"/></svg>
              JOIN NOW — FREE
            </span>
          </button>

          <button
            onClick={dismiss}
            data-testid="telegram-popup-skip"
            className="mt-3 text-slate-400 hover:text-white text-xs font-semibold tracking-widest uppercase transition-colors"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon, text }) {
  return (
    <div className="flex items-center gap-3 rounded-xl px-3.5 py-2.5 bg-white/5 border border-white/10">
      <span className="text-lg leading-none">{icon}</span>
      <span className="text-slate-100 text-sm font-medium">{text}</span>
    </div>
  );
}
